import {
	Archive,
	ArchiveRestore,
	Edit,
	Eye,
	FileText,
	Lock,
	MoreHorizontal,
} from "lucide-react";

import type { Operations } from "@workspace/operations";
import { CheckOperation } from "@workspace/operations";
import { Badge } from "@workspace/ui/components/badge";
import {
	createColumnHelper,
	type ColumnDef,
} from "@workspace/ui/components/basic-data-table";
import { Button } from "@workspace/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";

import type { StaffMember } from "../types";

export type StaffMemberRow = StaffMember & Operations;

const columnHelper = createColumnHelper<StaffMemberRow>();

export const columns = [
	columnHelper.accessor((x) => x.staffNumber, {
		header: "Staff Number",
		sortUndefined: "last",
		sortDescFirst: false,
		enableColumnFilter: false,
	}),
	columnHelper.accessor((x) => x.personalDetails.preferredName, {
		header: "Name",
		sortUndefined: "last",
		sortDescFirst: false,
		enableColumnFilter: false,
	}),
	columnHelper.accessor((x) => x.role.label, {
		header: "Role",
		meta: {
			filterVariant: "select",
		},
	}),
	columnHelper.accessor((x) => x.status, {
		enableSorting: false,
		header: "Status",
		meta: {
			filterVariant: "select",
		},
		cell: ({ cell, row }) => {
			// HA-283 — archiving is per-venue, so it overrides the tenant-wide status here rather
			// than replacing it: the member may still be Active at another venue.
			const status = row.original.isArchived
				? "Archived"
				: cell.getValue<string>();

			return (
				<Badge
					variant="outline"
					className={cn("capitalize", {
						"border-success": status === "Active",
						"border-muted": status === "Inactive",
						"border-destructive text-muted-foreground": status === "Archived",
					})}
				>
					{status}
				</Badge>
			);
		},
	}),
	columnHelper.display({
		id: "actions",
		header: "Actions",
		cell: ({ row, table }) => {
			return (
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<Button variant="ghost" className="h-8 w-8 p-0">
								<span className="sr-only">Open menu</span>
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						}
					/>
					<DropdownMenuContent align="end">
						<DropdownMenuLabel className="sr-only">Actions</DropdownMenuLabel>
						<CheckOperation
							operation="venue-management-update-staff-member"
							operations={row.original.operations}
						>
							<DropdownMenuItem
								onClick={() =>
									table.options.meta?.handleAction?.(row.index, "view")
								}
							>
								<Eye className="size-4" />
								View
							</DropdownMenuItem>
						</CheckOperation>
						<CheckOperation
							operation="venue-management-update-staff-member"
							operations={row.original.operations}
						>
							<DropdownMenuItem
								onClick={() =>
									table.options.meta?.handleAction?.(row.index, "edit")
								}
							>
								<Edit className="size-4" />
								Edit
							</DropdownMenuItem>
						</CheckOperation>
						<CheckOperation
							operation={"venue-management-staff-add-contract"}
							operations={row.original.operations}
						>
							<DropdownMenuItem
								onClick={() =>
									table.options.meta?.handleAction?.(row.index, "add-contract")
								}
							>
								<FileText className="size-4" />
								Add Contract
							</DropdownMenuItem>
						</CheckOperation>
						<CheckOperation
							operation="venue-management-reset-staff-pin"
							operations={row.original.operations}
						>
							<DropdownMenuItem
								onClick={() =>
									table.options.meta?.handleAction?.(row.index, "reset-pin")
								}
							>
								<Lock className="size-4" />
								Reset PIN
							</DropdownMenuItem>
						</CheckOperation>
						{/* HA-283 AC1/AC7/AC8 — Owner-only, and the pair is mutually exclusive:
						    an archived member offers Restore, everyone else offers Archive. */}
						<CheckOperation
							operation="venue-management-archive-staff-member"
							operations={row.original.operations}
						>
							{row.original.isArchived ? (
								<DropdownMenuItem
									onClick={() =>
										table.options.meta?.handleAction?.(row.index, "restore")
									}
								>
									<ArchiveRestore className="size-4" />
									Restore
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem
									variant="destructive"
									onClick={() =>
										table.options.meta?.handleAction?.(row.index, "archive")
									}
								>
									<Archive className="size-4" />
									Archive
								</DropdownMenuItem>
							)}
						</CheckOperation>
					</DropdownMenuContent>
				</DropdownMenu>
			);
		},
	}),
] as ColumnDef<StaffMemberRow>[];
